import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Mail, Lock, User } from 'lucide-react';

interface AuthFormProps {
  onAuthSuccess: (session: any) => void;
}

export function AuthForm({ onAuthSuccess }: AuthFormProps) {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validateEmail = (email: string) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email.toLowerCase());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!validateEmail(email)) {
        throw new Error('Please enter a valid email address');
      }

      if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) throw error;
        if (data.session) {
          onAuthSuccess(data.session);
        }
      } else {
        // Check if username is already taken
        const { data: existingUser, error: checkError } = await supabase
          .from('profiles')
          .select('id')
          .eq('username', username)
          .maybeSingle();

        if (checkError) throw checkError;
        if (existingUser) {
          throw new Error('Username already taken');
        }

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              username,
              first_name: firstName,
              last_name: lastName,
            },
          },
        });
        
        if (error) throw error;
        
        if (data.user) {
          const { error: profileError } = await supabase
            .from('profiles')
            .insert([
              {
                id: data.user.id,
                username,
                email,
                first_name: firstName,
                last_name: lastName,
                subscription_tier: 'free'
              },
            ]);
            
          if (profileError) throw profileError;
        }

        if (data.session) {
          onAuthSuccess(data.session);
        } else {
          setError('Please check your email for verification');
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-custom-orange-50/40 via-custom-orange-100/40 to-custom-orange-200/40 flex items-center justify-center">
      <div className="container relative w-[850px] h-[650px] bg-white/80 backdrop-blur-sm rounded-[30px] shadow-xl overflow-hidden">
        <div className={`form-box absolute w-1/2 h-full bg-white/90 backdrop-blur-sm flex items-center ${isLogin ? 'right-0' : 'right-1/2'} transition-all duration-600 p-10 z-10 rounded-[30px]`}>
          <form onSubmit={handleSubmit} className="w-full text-center">
            <h1 className="text-3xl font-bold mb-8 bg-gradient-to-r from-custom-orange-300 to-custom-orange-400 bg-clip-text text-transparent">
              {isLogin ? 'Welcome Back!' : 'Create Account'}
            </h1>
            
            {error && (
              <div className="bg-red-50/50 backdrop-blur-sm text-red-400 p-3 rounded-2xl mb-4">
                {error}
              </div>
            )}

            {!isLogin && (
              <>
                <div className="relative mb-4">
                  <input
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full px-5 py-3 bg-custom-orange-50/50 backdrop-blur-sm rounded-2xl pl-12 focus:outline-none focus:ring-2 focus:ring-custom-orange-200/50 text-custom-orange-400 placeholder-custom-orange-300"
                    required
                    disabled={loading}
                    pattern="[a-zA-Z0-9_]{3,20}"
                    title="Username must be between 3 and 20 characters and can only contain letters, numbers, and underscores"
                  />
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-custom-orange-300" size={20} />
                </div>

                <div className="relative mb-4">
                  <input
                    type="text"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-5 py-3 bg-custom-orange-50/50 backdrop-blur-sm rounded-2xl pl-12 focus:outline-none focus:ring-2 focus:ring-custom-orange-200/50 text-custom-orange-400 placeholder-custom-orange-300"
                    required
                    disabled={loading}
                  />
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-custom-orange-300" size={20} />
                </div>

                <div className="relative mb-4">
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-5 py-3 bg-custom-orange-50/50 backdrop-blur-sm rounded-2xl pl-12 focus:outline-none focus:ring-2 focus:ring-custom-orange-200/50 text-custom-orange-400 placeholder-custom-orange-300"
                    required
                    disabled={loading}
                  />
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-custom-orange-300" size={20} />
                </div>
              </>
            )}

            <div className="relative mb-4">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-5 py-3 bg-custom-orange-50/50 backdrop-blur-sm rounded-2xl pl-12 focus:outline-none focus:ring-2 focus:ring-custom-orange-200/50 text-custom-orange-400 placeholder-custom-orange-300"
                required
                disabled={loading}
              />
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-custom-orange-300" size={20} />
            </div>

            <div className="relative mb-6">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-5 py-3 bg-custom-orange-50/50 backdrop-blur-sm rounded-2xl pl-12 focus:outline-none focus:ring-2 focus:ring-custom-orange-200/50 text-custom-orange-400 placeholder-custom-orange-300"
                required
                disabled={loading}
                minLength={6}
              />
              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-custom-orange-300" size={20} />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-custom-orange-300/80 to-custom-orange-400/80 backdrop-blur-sm text-white py-3 rounded-2xl font-semibold hover:from-custom-orange-400/90 hover:to-custom-orange-500/90 transition-all shadow-lg disabled:opacity-50"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Sign Up')}
            </button>
          </form>
        </div>

        <div className="toggle-box absolute w-full h-full bg-gradient-to-br from-custom-orange-300/80 to-custom-orange-400/80 backdrop-blur-sm flex rounded-[30px]">
          <div className={`toggle-panel w-1/2 h-full text-white flex flex-col justify-center items-center p-8 ${isLogin ? '' : 'translate-x-full'} transition-transform duration-600`}>
            <h1 className="text-3xl font-bold mb-4">{isLogin ? "New Here?" : 'Welcome Back!'}</h1>
            <p className="mb-6 text-lg text-custom-orange-50">
              {isLogin
                ? 'Join us and start your journaling journey today!'
                : 'Already have an account? Sign in to continue your journey.'}
            </p>
            <button
              onClick={() => setIsLogin(!isLogin)}
              disabled={loading}
              className="px-8 py-2 border-2 border-white/80 backdrop-blur-sm text-white rounded-2xl hover:bg-white/20 transition-all disabled:opacity-50"
            >
              {isLogin ? 'Sign Up' : 'Sign In'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}